// THE representation gate for every report. A report must never show a year a client wasn't ours.
//
// ⛔ WHY THIS EXISTS AS A SHARED HELPER. Six RPCs (expected_bills, pull_parcels_by_state, both
// rolls, parcel_states, parcel_states_summary) gate on SQL is_represented(). Notice Tracking (now src/reports/cards/NoticeProgress.jsx) was given
// the same gate by hand on 2026-08-23. The old PropertyExecutiveSummary page (now deleted) had NO
// year test of any kind — it selected id/property_id/parcel_name and nothing else, so it counted
// every parcel in every year. That is the bug that put 5 parcels into LaSalle's 2021 when only 30
// of 35 were ours, and showed them as work we owed. Every report goes through this file now instead
// of each one remembering to do it.
//
// ⛔ THE CONVENTION (changed 2026-08-23, 2026-08-23_retired_year_exclusive.sql). All three columns
// name the FIRST year of a new state:
//     first_year       = first year represented
//     retired_year     = first year NOT represented   ← EXCLUSIVE
//     reactivated_year = first year represented again
//   represented = [first_year, retired_year) ∪ [reactivated_year, ∞)
// The arithmetic lives in src/utils/retirement.js, which mirrors the SQL. Do not re-derive it here.
//
// ⛔ CASCADE. A parcel counts only when it AND its property both do — the same paired AND every
// RPC applies. A live parcel under a retired property is not represented.
//
// ⛔ JOIN ON property_id, NOT master_property_id. Every SQL caller does
// `join public.properties pr on pr.id = p.property_id`. master_property_id is a denormalised
// labelling key used by the roll-ups; using it here is how a report starts disagreeing with Tax
// Bill Processing about the same parcel.
//
// ⛔ FAIL OPEN. A property_id resolving to no properties row counts as REPRESENTED, consistent
// with every other caller — a mapping gap must not silently delete a parcel from the totals. It is
// LOGGED once per id so the gap stays findable rather than quietly counted.

import { representedInYear, yearState } from '../utils/retirement';
import { fetchAll } from './pagination';

// The three columns a parcel must carry for the gate to work. Any report SELECT that omits these
// silently passes every parcel — representedInYear treats missing values as "always ours".
export const PARCEL_REPRESENTATION_COLS = 'first_year, retired_year, reactivated_year';
export const PROPERTY_REPRESENTATION_COLS = 'id, first_year, retired_year, reactivated_year';

// Map<property_id, {first_year, retired_year, reactivated_year}> for the whole table.
// Loaded once per report run and reused across years — the columns are year-independent facts.
export async function loadPropertyRepresentation() {
  const rows = await fetchAll('properties', PROPERTY_REPRESENTATION_COLS);
  const m = new Map();
  rows.forEach((pr) => m.set(pr.id, pr));
  return m;
}

// Gate a parcel list for ONE year.
//   parcels  : rows carrying property_id + the three representation columns
//   propRep  : Map from loadPropertyRepresentation()
//   year     : the value year being reported
// Returns { rows, excluded, unresolved } — `unresolved` is the set of property_ids that resolved
// to nothing (all counted as represented, all logged).
export function gateParcels(parcels, propRep, year, label = 'report') {
  const unresolved = new Set();
  const rows = (parcels || []).filter((p) => {
    const pr = p.property_id ? propRep.get(p.property_id) : null;
    if (p.property_id && !pr && !unresolved.has(p.property_id)) {
      unresolved.add(p.property_id);
      console.warn(`[${label}] property_id resolves to no properties row:`, p.property_id,
        '— parcel', p.account_number || p.id, 'counted as REPRESENTED (fail-open)');
    }
    return representedInYear(p, pr, year);
  });
  const excluded = (parcels || []).length - rows.length;
  if (excluded > 0) {
    console.info(`[${label}] ${excluded} parcel(s) excluded from ${year} — not represented that year`);
  }
  // ⛔ BASELINE IDS, RETURNED ALONGSIDE — an ADDED key, so every existing caller is unaffected.
  // A baseline year is one we were NOT engaged in but which may hold the client's own prior figures,
  // loaded so savings have something to measure against. Excluding them made that data write-only:
  // enterable in the parcel modal and invisible on the report that is the reason for entering it.
  //
  // ⛔ NOT ADDED TO `rows`. `rows` means "represented", and every other caller of this function uses
  // it to decide what is ours. Folding baseline parcels in would silently widen every one of them.
  const baselineIds = new Set((parcels || []).filter((p) => {
    const pr = p.property_id ? propRep.get(p.property_id) : null;
    return yearState(p, pr, year) === 'baseline';
  }).map((p) => p.id));
  return { rows, excluded, unresolved, baselineIds };
}

// Gate a parcel list across MANY years at once — the shape a multi-year report needs.
// Returns Map<year, parcels[]> plus the totals, so a caller can report coverage per year.
export function gateParcelsByYear(parcels, propRep, years, label = 'report') {
  const byYear = new Map();
  let excluded = 0;
  const unresolved = new Set();
  (years || []).forEach((y) => {
    const r = gateParcels(parcels, propRep, y, label);
    byYear.set(String(y), r.rows);
    excluded += r.excluded;
    r.unresolved.forEach((id) => unresolved.add(id));
  });
  return { byYear, excluded, unresolved };
}

// Predicate form, for a report that has already loaded the property map and wants to test one row
// inline (e.g. while folding parcel-year rows). Same cascade, same fail-open, no logging.
export const isParcelRepresented = (parcel, propRep, year) =>
  representedInYear(parcel, parcel?.property_id ? propRep.get(parcel.property_id) || null : null, year);
